import { z } from "zod";

/**
 * Yönetim paneli: katalog (kategori, hizmet) ve ücret kuralları (K-006, K-012, K-050, K-092).
 * Silme yok: kapatılır (is_active) ya da ücret kuralı sona erdirilir; eski işler ve faturalar bozulmaz.
 */

const CODE = /^[a-z][a-z0-9_]{1,62}$/;
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const LOCALE = /^[a-z]{2}(?:-[A-Z]{2})?$/;
/** seed kimlikleri uuid v5, demo kimlikleri sabit: sürüm denetlenmez */
const ID = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/);

export const CategoryTranslation = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000).nullish(),
  /** boşsa addan üretilir */
  slug: z.string().trim().regex(SLUG).max(140).nullish(),
});
export const ServiceTranslation = z.object({
  name: z.string().trim().min(1).max(120),
  shortDescription: z.string().trim().max(300).nullish(),
  description: z.string().trim().max(5000).nullish(),
  slug: z.string().trim().regex(SLUG).max(140).nullish(),
});

const translations = <T extends z.ZodTypeAny>(t: T) =>
  z
    .record(z.string().regex(LOCALE), t)
    .refine((r) => Object.keys(r).length > 0 && Object.keys(r).length <= 20, {
      message: "translations_required",
    });

export const CreateCategoryBody = z.object({
  code: z.string().regex(CODE),
  icon: z.string().trim().max(64).nullish(),
  sort: z.number().int().min(0).max(32000).nullish(),
  isActive: z.boolean().default(true),
  translations: translations(CategoryTranslation),
});
export type CreateCategoryBody = z.infer<typeof CreateCategoryBody>;

export const UpdateCategoryBody = z
  .object({
    icon: z.string().trim().max(64).nullable(),
    sort: z.number().int().min(0).max(32000).nullable(),
    isActive: z.boolean(),
    translations: translations(CategoryTranslation),
  })
  .partial()
  .refine((b) => Object.keys(b).length > 0, { message: "empty" });
export type UpdateCategoryBody = z.infer<typeof UpdateCategoryBody>;

const serviceFields = {
  categoryIds: z.array(ID).min(1).max(10),
  formTemplateId: ID,
  pricingType: z.enum(["hourly", "fixed"]),
  locationModes: z.enum(["customer", "provider", "both"]),
  urgentEnabled: z.boolean(),
  /** müşteri teklif isteyebilir (K-059) */
  quotesEnabled: z.boolean(),
  requiredLicense: z.string().trim().regex(CODE).max(32).nullable(),
  /** geçerli Poji Verified rozeti ister; çocuk hizmetleri (K-170) */
  requiresConduct: z.boolean(),
  taxCategoryId: ID.nullable(),
  defaultMinMinutes: z
    .number()
    .int()
    .min(15)
    .max(24 * 60)
    .nullable(),
  icon: z.string().trim().max(64).nullable(),
  isActive: z.boolean(),
  /** hangi pazarda açık (K-032) */
  markets: z.array(z.object({ marketId: ID, isActive: z.boolean() })).max(50),
  translations: translations(ServiceTranslation),
};

export const CreateServiceBody = z.object({
  code: z.string().regex(CODE),
  ...serviceFields,
  requiredLicense: serviceFields.requiredLicense.default(null),
  requiresConduct: serviceFields.requiresConduct.default(false),
  taxCategoryId: serviceFields.taxCategoryId.default(null),
  defaultMinMinutes: serviceFields.defaultMinMinutes.default(null),
  icon: serviceFields.icon.default(null),
  isActive: serviceFields.isActive.default(true),
  urgentEnabled: serviceFields.urgentEnabled.default(false),
  quotesEnabled: serviceFields.quotesEnabled.default(false),
});
export type CreateServiceBody = z.infer<typeof CreateServiceBody>;

export const UpdateServiceBody = z
  .object(serviceFields)
  .partial()
  .refine((b) => Object.keys(b).length > 0, { message: "empty" });
export type UpdateServiceBody = z.infer<typeof UpdateServiceBody>;

/** Panelin oluşturabildiği ücret türleri; müşteriye/hizmet verene özel kurallar yalnızca listelenir. */
export const ADMIN_FEE_KINDS = [
  "commission",
  "urgent_fee",
  "service_fee",
  "trust_support_fee",
] as const;

/**
 * Yeni ücret kuralı. `replacesId` verilirse eski kural yeni kuralın başladığı anda kapanır (sürüm):
 * kural yerinde değiştirilmez, böylece geçmiş işlerin hangi oranla fiyatlandığı her zaman görünür.
 */
export const CreateFeeRuleBody = z
  .object({
    marketId: ID,
    kind: z.enum(ADMIN_FEE_KINDS),
    categoryId: ID.nullable().default(null),
    serviceId: ID.nullable().default(null),
    providerType: z.enum(["individual", "company"]).nullable().default(null),
    rateBps: z.number().int().min(0).max(10000).nullable().default(null),
    fixedMinor: z
      .string()
      .regex(/^\d{1,9}$/)
      .nullable()
      .default(null),
    providerShareBps: z.number().int().min(0).max(10000).nullable().default(null),
    /** boşsa hemen */
    effectiveFrom: z.string().datetime({ offset: true }).nullable().default(null),
    name: z.string().trim().max(120).nullable().default(null),
    replacesId: ID.nullable().default(null),
  })
  .superRefine((b, ctx) => {
    if (b.categoryId && b.serviceId)
      ctx.addIssue({ code: "custom", path: ["serviceId"], message: "category_or_service" });
    if (b.kind === "urgent_fee") {
      if (b.fixedMinor === null)
        ctx.addIssue({ code: "custom", path: ["fixedMinor"], message: "required" });
      if (b.providerShareBps === null)
        ctx.addIssue({ code: "custom", path: ["providerShareBps"], message: "required" });
      if (b.rateBps !== null)
        ctx.addIssue({ code: "custom", path: ["rateBps"], message: "not_allowed" });
    } else {
      if (b.rateBps === null && b.fixedMinor === null)
        ctx.addIssue({ code: "custom", path: ["rateBps"], message: "rate_or_fixed" });
      if (b.providerShareBps !== null)
        ctx.addIssue({ code: "custom", path: ["providerShareBps"], message: "not_allowed" });
    }
  });
export type CreateFeeRuleBody = z.input<typeof CreateFeeRuleBody>;

export const EndFeeRuleBody = z.object({
  /** boşsa hemen */
  at: z.string().datetime({ offset: true }).nullable().default(null),
});

// ---------------------------------------------------------------- çeviriler (catalog.translations.manage)

/** Panelde çevirisi düzenlenebilen kayıt türleri ve alanları (ilk alan zorunlu ad/etiket) */
export const TRANSLATION_KINDS = {
  category: { fields: ["name", "description"] },
  service: { fields: ["name", "shortDescription", "description"] },
  question: { fields: ["label", "helpText", "placeholder"] },
  option: { fields: ["label"] },
  region: { fields: ["name"] },
  locality: { fields: ["name", "localIntro"] },
} as const;
export type TranslationKind = keyof typeof TRANSLATION_KINDS;
export const TranslationKindSchema = z.enum(
  Object.keys(TRANSLATION_KINDS) as [TranslationKind, ...TranslationKind[]],
);

/** Bir kaydın bir dildeki çevirisi. undefined = dokunma, null = boşalt (ad/etiket boşaltılamaz). */
export const SaveTranslationBody = z
  .object({
    name: z.string().trim().min(1).max(300),
    label: z.string().trim().min(1).max(300),
    description: z.string().trim().max(5000).nullable(),
    shortDescription: z.string().trim().max(300).nullable(),
    helpText: z.string().trim().max(2000).nullable(),
    placeholder: z.string().trim().max(300).nullable(),
    localIntro: z.string().trim().max(5000).nullable(),
  })
  .partial()
  .refine((b) => Object.keys(b).length > 0, { message: "empty" });
export type SaveTranslationBody = z.infer<typeof SaveTranslationBody>;

// ---------------------------------------------------------------- formlar (catalog.forms.manage, K-036)

export const QUESTION_KINDS = [
  "address",
  "secondary_address",
  "task_size",
  "vehicle",
  "description",
  "single_choice",
  "multi_choice",
  "text",
  "long_text",
  "number",
  "date",
  "routing",
] as const;
export type QuestionKind = (typeof QUESTION_KINDS)[number];
/** Seçenekli soru türleri: en az bir seçenek gerekir */
export const CHOICE_KINDS: readonly QuestionKind[] = [
  "task_size",
  "vehicle",
  "single_choice",
  "multi_choice",
  "routing",
];

const FCODE = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/);

export const OptionInput = z.object({
  code: FCODE,
  /** pazarın varsayılan dilinde (en); diğer diller Çeviriler ekranından */
  label: z.string().trim().min(1).max(300),
  estimatedMinutes: z
    .number()
    .int()
    .min(0)
    .max(24 * 60)
    .nullable()
    .default(null),
  acceptsText: z.boolean().default(false),
});

const questionLimits = {
  isRequired: z.boolean(),
  section: z.string().trim().max(64).nullable(),
  minValue: z.number().nullable(),
  maxValue: z.number().nullable(),
  maxLength: z.number().int().min(1).max(10000).nullable(),
  label: z.string().trim().min(1).max(300),
  helpText: z.string().trim().max(2000).nullable(),
  placeholder: z.string().trim().max(300).nullable(),
};

export const CreateFormBody = z.object({
  code: FCODE,
  kind: z.enum(["stepwise", "questionnaire"]).default("stepwise"),
  /** bu formun sorularını kopyala (yeni hizmet için benzer form) */
  copyFromId: ID.nullable().default(null),
});
export type CreateFormBody = z.input<typeof CreateFormBody>;

export const AddQuestionBody = z
  .object({
    code: FCODE,
    kind: z.enum(QUESTION_KINDS),
    ...questionLimits,
    isRequired: questionLimits.isRequired.default(true),
    section: questionLimits.section.default(null),
    minValue: questionLimits.minValue.default(null),
    maxValue: questionLimits.maxValue.default(null),
    maxLength: questionLimits.maxLength.default(null),
    helpText: questionLimits.helpText.default(null),
    placeholder: questionLimits.placeholder.default(null),
    options: z.array(OptionInput).max(50).default([]),
  })
  .superRefine((b, ctx) => {
    if (CHOICE_KINDS.includes(b.kind) && b.options.length === 0)
      ctx.addIssue({ code: "custom", path: ["options"], message: "options_required" });
    if (!CHOICE_KINDS.includes(b.kind) && b.options.length > 0)
      ctx.addIssue({ code: "custom", path: ["options"], message: "options_not_allowed" });
    if (new Set(b.options.map((o) => o.code)).size !== b.options.length)
      ctx.addIssue({ code: "custom", path: ["options"], message: "duplicate_code" });
    if (b.minValue !== null && b.maxValue !== null && b.minValue > b.maxValue)
      ctx.addIssue({ code: "custom", path: ["maxValue"], message: "min_over_max" });
  });
export type AddQuestionBody = z.input<typeof AddQuestionBody>;

/** Soru türü ve kodu değişmez (eski cevaplar onlara bağlı); gerekirse yeni soru eklenip eskisi kaldırılır */
export const UpdateQuestionBody = z
  .object({ ...questionLimits, isActive: z.boolean() })
  .partial()
  .refine((b) => Object.keys(b).length > 0, { message: "empty" });
export type UpdateQuestionBody = z.infer<typeof UpdateQuestionBody>;

export const UpdateOptionBody = z
  .object({
    label: z.string().trim().min(1).max(300),
    estimatedMinutes: z
      .number()
      .int()
      .min(0)
      .max(24 * 60)
      .nullable(),
    acceptsText: z.boolean(),
    isActive: z.boolean(),
  })
  .partial()
  .refine((b) => Object.keys(b).length > 0, { message: "empty" });

export const MoveBody = z.object({ direction: z.enum(["up", "down"]) });

// ---------------------------------------------------------------- iptal kuralları (K-057; settings.markets.manage)

/** Kategoriye ya da hizmete özel iptal kuralı. Pazarın genel kuralı Pazar ayarlarında (K-022). */
export const CreateCancellationPolicyBody = z
  .object({
    marketId: ID,
    categoryId: ID.nullable().default(null),
    serviceId: ID.nullable().default(null),
    name: z.string().trim().max(120).nullable().default(null),
    /** işe bu kadar saat kalana kadar ücretsiz (ör. etkinlikte 168 = 7 gün) */
    freeCancelHours: z.number().int().min(0).max(2160),
    /** geç iptal ücreti: saat ücreti × bu kadar saat; boş = pazarınki */
    cancellationFeeHours: z
      .number()
      .min(0)
      .max(8)
      .refine((n) => Math.abs(Math.round(n * 4) - n * 4) < 1e-6, { message: "quarter_hours" })
      .nullable()
      .default(null),
    /** sabit fiyatlı işte geç iptal ücreti: tutarın yüzdesi (bps, 2500 = %25); boş = pazarınki (K-058) */
    cancellationFeeBps: z.number().int().min(0).max(10000).nullable().default(null),
    effectiveFrom: z.string().datetime({ offset: true }).nullable().default(null),
    replacesId: ID.nullable().default(null),
  })
  .superRefine((b, ctx) => {
    if (!b.categoryId === !b.serviceId)
      ctx.addIssue({ code: "custom", path: ["categoryId"], message: "category_xor_service" });
  });
export type CreateCancellationPolicyBody = z.input<typeof CreateCancellationPolicyBody>;

// ---------------------------------------------------------------- bölgeler ve yerleşim yerleri (K-025)

const coords = {
  latitude: z.number().min(-90).max(90).nullable(),
  longitude: z.number().min(-180).max(180).nullable(),
  /** ör. "SLM, STJ" (Malta posta kodu ön ekleri) */
  postcodePrefixes: z
    .string()
    .trim()
    .max(64)
    .regex(/^[A-Z]{2,4}(, ?[A-Z]{2,4})*$|^$/)
    .nullable(),
};

export const CreateLocalityBody = z.object({
  regionId: ID,
  code: z.string().regex(/^[a-z][a-z0-9-]{1,62}$/),
  /** varsayılan dilde ad; diğer diller Çeviriler ekranından */
  name: z.string().trim().min(1).max(120),
  latitude: coords.latitude.default(null),
  longitude: coords.longitude.default(null),
  postcodePrefixes: coords.postcodePrefixes.default(null),
});
export type CreateLocalityBody = z.input<typeof CreateLocalityBody>;

export const UpdateLocalityBody = z
  .object({ ...coords, isActive: z.boolean() })
  .partial()
  .refine((b) => Object.keys(b).length > 0, { message: "empty" });
